import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  queryStatus,
  ShowToast,
  TaskStatus,
  PatternsErrorHandler,
} from '../../model';
import type { HttpClient } from '../../model/httpClient';
import { Translate, TranslationsKeys } from '../../services';
import { ModalState } from '../ModalState';
import { ConditionalModalState } from '../ConditionalModalState';
import { CreateExportAsyncJobRequest } from '@wix/bex-utils/@wix/ambassador-fedinfra-exportservice-v1-export-async-job/types';
import {
  JobStatus,
  WritePolicy,
  type Destination,
  type DataMovementJob,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';
import { ParseCsvResult, parseCsv, unparseCsv } from './parseCsv';
import { rewriteParsedCsvForImport } from './rewriteCsv';
import { createHubJobClient, HubJobClient } from './hubJobClient';
import { pollImportJob } from './importJobPoller';
import {
  detectColumnType,
  toPatternsFieldType,
  TYPE_SAMPLE_SIZE,
  type DetectColumnType,
  type NewFieldType,
} from './detectFieldType';
import type { Field, FieldSpec, NonReferenceField } from '../../types/Field';
import type { Schema } from '../../types/Schema';
import type {
  FieldDraft,
  FieldManagement,
  FieldPermissions,
  FieldsSource,
} from '../../types/FieldsSource';
import { toDataExtensionFieldEdit, toFieldEdit } from './fieldEdit';

export interface ImportCollection {
  clearResultAndMoveToStart(params?: { force?: boolean }): void;
  /** The collection's query name — its id, used as the CMS import destination. */
  readonly queryName: string;
}

/** Outcome of a server-side import job, derived from the job's progress. */
export interface ImportResult {
  succeeded: number;
  failed: number;
  /** New items created in the destination (subset of `succeeded`). */
  created: number;
  /** Existing items updated in the destination (subset of `succeeded`). */
  updated: number;
}

/** A rejected row: its index into `csvRows` and the server's failure reason. */
interface FailedRow {
  index: number;
  reason: string;
}

export interface BackupResult {
  /**
   * Reverts the collection to its pre-import state. Called only when the user
   * cancels mid-import (matching the previous client-side behavior); a job
   * failure leaves partial rows in place and offers "Try again".
   *
   * Must fully revert to the snapshot taken before the import — including rows
   * the import overwrote in place. The default `writePolicy` is upsert (OVERWRITE
   * by `_id`), so it is NOT enough to delete newly-inserted ids; previously
   * existing rows may have been overwritten and must be restored to their prior
   * values.
   */
  restore(signal: AbortSignal): Promise<void>;
}

/**
 * Optional snapshot taken before the import runs, returning a `restore` used to
 * undo the import on cancel. See {@link BackupResult.restore} for the revert
 * contract under upsert.
 */
export type BackupFn = (signal: AbortSignal) => Promise<BackupResult | void>;

export type DownloadCsvFn = (content: string, fileName: string) => void;

/** The application error from a failed import job, handed to a resolver. */
export type ImportJobApplicationError = {
  code?: string;
  description?: string;
  /** Any structured payload the server attached to the error (e.g. a field list). */
  data?: Record<string, any>;
};

/**
 * Consumer-supplied map from a failed import job's application-error code to a
 * resolver that returns the message to show. An import runs server-side, so a
 * failure arrives on a 200 (the poll succeeds; the job doesn't) with the
 * application error in the job status. The consumer supplies only the
 * code→message map (their codes, their translations) — ImportState applies it
 * through the container's `errorHandler` internally, so the progress step can
 * resolve the message via `getResolvedError`. The resolver receives the job's
 * application error (its `code`/`description`/`data`):
 *
 *   jobApplicationErrorMap: {
 *     [jobErrorCode]: (error) => ({ message: translate(..., error.data) }),
 *   }
 *
 * When omitted, a failed job falls back to the generic error message.
 */
export type JobApplicationErrorMap = Record<
  string,
  (error: ImportJobApplicationError) => { message: string }
>;

// `required` is added on top of the ambassador descriptor (which carries only
// `id`/`header`): a field the destination mandates. Consumers set it — the
// schema-backed config forwards `validation.required`, manual/platformized
// configs set it per field — and the mapping step blocks import while any
// required field has no column mapped to it (CAIRO-4452).
export type ImportField = NonNullable<
  CreateExportAsyncJobRequest['fields']
>[number] & { required?: boolean };

// `NewFieldType`, `detectColumnType`, etc. are re-exported from the package via
// the `ImportState` barrel (`./index`), which also exports `./detectFieldType`.

// Re-export the write-policy enum so consumers (and the cairo UI) reference it
// from `@wix/bex-core` rather than the bundled ambassador path.
export { WritePolicy } from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';
// Re-export the destination type for platformized consumers that supply their
// own variant (e.g. `{ storesCatalogProducts: {} }`) — same reason as above.
export type { Destination } from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';

// Per-type configuration for a new field, captured from the "Edit field" editor
// (CAIRO-4398). All optional — an unedited field carries none and is created
// from detected defaults only. `displayName` is intentionally not here: the
// (possibly user-overridden) name is deduped and surfaced as the field def's
// `displayName`.
//
// This is core-internal storage on the column mapping, shared by both sources —
// the CMS editor fills `validation`/`isPii`/`helpText`, the DataExtension editor
// fills `permissions`/`isPii`/`helpText`/`deType`. The two never cross: which
// subset is read is decided by the config's `dataSource` branch when the field
// def is built (`_resolveFieldMapping`). The consumer-facing create callbacks
// still receive distinct def types (`NewFieldDefinition` vs
// `NewDataExtensionFieldDef`), so this superset is not part of the public API.
export interface NewFieldConfig {
  validation?: NonReferenceField['validation'];
  isPii?: boolean;
  helpText?: string;
  /** DataExtension-only: read/write permission defaults chosen in the editor. */
  permissions?: FieldPermissions;
  /**
   * DataExtension-only: the precise DataExtension field-type id the user picked
   * (e.g. `'decimal'`), finer than the coarse detected `NewFieldType`. Absent ⇒
   * the type is derived from the column's detected `NewFieldType`.
   */
  deType?: string;
}

// A new field to add to the collection schema during import. `key` is decided by
// Cairo (derived from the column header — or the user's name override — deduped)
// and is also the key the column's values are written under, so the consumer
// must add the field under exactly this key. `config` carries any per-type
// configuration the field editor produced; create the field with it.
export interface NewFieldDefinition {
  key: string;
  type: NewFieldType;
  displayName: string;
  config?: NewFieldConfig;
}

/**
 * Consumer-supplied callback that adds the given new fields to the collection
 * schema in a single call, before the rows are uploaded. Mirrors the cm-import
 * `beforeImport`/`updateSchema` flow: Cairo decides the fields (and their keys),
 * the consumer performs one schema mutation (e.g. `updateSchema(collectionId,
 * ...addField(key, config))`). Its presence is also the opt-in for the "create
 * new field" default — see `getDefaultMapping`.
 *
 * Optionally returns the collection's updated fields map, mirroring the schema's
 * `FieldActions`. When it does, the import merges that map into the table's field
 * source on success so the new columns appear with no manual reload (CAIRO-4400).
 * Returning `void` still imports fine — the columns just wait for the next schema
 * load.
 */
export type UpdateSchemaFn = (
  fields: NewFieldDefinition[],
) => Promise<Schema['fields'] | void>;

/**
 * Creates the pending new DataExtension custom fields before the rows are
 * uploaded — the `destination` branch's counterpart to `updateSchema`. Its
 * presence is the "create new field" opt-in for a DataExtension import. Receives
 * the source-neutral pending columns (Cairo-decided keys + names + the edited
 * config); the DataExtension source builds its own field shape from them, so no
 * DataExtension-specific shape leaks into core.
 * Optionally returns the updated fields map to merge into the table (CAIRO-4400);
 * DataExtension refreshes its own schema in place, so returning `void` is fine.
 */
export type CreateDataExtensionFieldsFn = (
  fields: PendingNewField[],
) => Promise<Schema['fields'] | void>;

/**
 * How a CSV column is mapped:
 * - `existing` — into an existing collection field by id
 * - `new` — into a new field created during import. `fieldType` is the detected
 *   type, or the type the user picked in the field editor. `displayName` and
 *   `config` are present only once the user edited the pending field via the
 *   "Edit field" editor — `displayName` overrides the header-derived name and
 *   `config` carries the per-type configuration.
 * - `null` — don't import (skip)
 */
export type ColumnMapping =
  | { kind: 'existing'; fieldId: string }
  | {
      kind: 'new';
      fieldType: NewFieldType;
      displayName?: string;
      config?: NewFieldConfig;
    }
  | null;

/** A `new`-mapped column with its deduped key + display name, handed to a
 *  source's create callback so it can build its own field shape. */
export type PendingNewField = {
  key: string;
  displayName: string;
  mapping: Extract<ColumnMapping, { kind: 'new' }>;
};

/**
 * Where the import writes and how it creates new fields, split per source. The
 * discriminant (`collectionId` vs `destination`) makes the two mutually
 * exclusive and pairs each target with its own create-fields callback, so a CMS
 * target can't be paired with a DataExtension create callback (or vice versa).
 * Each branch's create members are optional — a plain import supplies none and
 * is mapping-only.
 */
export type ImportDataSource =
  | {
      /** Wix Data collection the import writes into (the common CMS case). */
      collectionId: string;
      /** Creates new schema fields before upload; resolves with the updated map. */
      updateSchema?: UpdateSchemaFn;
    }
  | {
      /**
       * Platformized (non–Wix-Data) destination, sent to the job verbatim (e.g.
       * `{ storesCatalogProducts: {} }`). `writePolicy` is never attached to it.
       */
      destination: Destination;
      /** Creates new DataExtension custom fields before upload. */
      createFields?: CreateDataExtensionFieldsFn;
      /**
       * The source's field-management contract, used by the DataExtension
       * "create/edit field" editor (the field modal, permissions, existing keys).
       * Present when the source permits adding fields.
       */
      fieldManagement?: FieldManagement;
    };

/**
 * The effective import config. Satisfied both by a plain object (a fully
 * hand-built config) and by the object `buildImportConfig` assembles, whose
 * members are getters read on demand — so the async schema load needn't resolve
 * before `ImportState` is built. `ImportState` holds it by reference (never
 * spreads it) so those getters stay live.
 */
export interface ResolvedImportConfig {
  /** Existing collection fields, for column auto-mapping. */
  fields: ImportField[];
  /** Where the import writes, and how it creates new fields — see {@link ImportDataSource}. */
  dataSource: ImportDataSource;
  /**
   * Optional snapshot taken before the rows are uploaded. The returned
   * `restore` is invoked only if the user cancels mid-import — a job failure
   * leaves partial rows and offers "Try again" instead.
   */
  backup?: BackupFn;
  /**
   * Merges a create callback's returned fields map into the table after a
   * successful import, so the new columns appear with no reload (CAIRO-4400). A
   * schema-backed config (`buildImportConfig`) wires this to its live schema;
   * when omitted, the new columns wait for the next schema load.
   */
  mergeFields?: (fields: Schema['fields']) => void;
  /** See {@link JobApplicationErrorMap}. */
  jobApplicationErrorMap?: JobApplicationErrorMap;
  /**
   * The collection's id-field key, for the id-collision write policy. Defaults
   * to `_id` (the Wix Data default) when unset.
   */
  idFieldKey?: string;
}

/**
 * The inputs a consumer passes to `<ImportButton config={...} />`. Everything the
 * table's source provides — the collection, its fields, create-new-field and
 * merge-back — is read live off the `fieldsSource`, so only what isn't derivable
 * lives here. Omit `destination` to import into the table's own collection (Wix
 * Data); set it to write to a platformized target instead.
 */
export interface ImportConfig {
  /** A platformized (non–Wix-Data) destination, e.g. `{ storesCatalogProducts: {} }`. */
  destination?: Destination;
  /** Existing fields for column auto-mapping, merged with the source's live
   *  fields (these first, deduped by id). */
  fields?: ImportField[];
  /** Snapshot taken before the rows upload; its `restore` undoes a cancelled import. */
  backup?: BackupFn;
  /** The collection's id-field key, for the id-collision write policy. Defaults to `_id`. */
  idFieldKey?: string;
  /** See {@link JobApplicationErrorMap}. */
  jobApplicationErrorMap?: JobApplicationErrorMap;
}

// Builds the Field to create from a pending new column: import decides the key,
// and the detected NewFieldType maps to the schema's PatternsFieldType. Import
// only ever creates non-reference fields, so the cast is safe.
const toField = (def: NewFieldDefinition): Field =>
  ({
    id: def.key,
    displayName: def.displayName,
    type: toPatternsFieldType(def.type),
    ...(def.config?.validation && { validation: def.config.validation }),
    ...(def.config?.isPii && { isPii: true }),
    ...(def.config?.helpText && { helpText: def.config.helpText }),
  } as Field);

/**
 * Assembles a `ResolvedImportConfig` from the table's live `fieldsSource` and the
 * consumer `inputs`. The schema-derived members are getters so they read the
 * source live — fields import creates and fields added through the
 * field-management panel show up immediately. Empty before a source attaches;
 * nothing imports until then.
 *
 * Exported for tests.
 */
export const buildImportConfig = (
  fieldsSource: FieldsSource | undefined,
  collection: ImportCollection,
  inputs: ImportConfig = {},
): ResolvedImportConfig => {
  const {
    destination,
    fields = [],
    backup,
    idFieldKey,
    jobApplicationErrorMap,
  } = inputs;
  return {
    get fields(): ImportField[] {
      const seen = new Set(fields.map((f) => f.id));
      const live = (fieldsSource?.fields ?? [])
        .filter((f) => !seen.has(f.id))
        .map((f) => ({
          id: f.id,
          header: f.displayName,
          required: f.validation?.required,
        }));
      return [...fields, ...live];
    },
    get dataSource(): ImportDataSource {
      if (destination) {
        return { destination };
      }
      const add = fieldsSource?.fieldManagement?.canAddFields
        ? fieldsSource.fieldActions?.addFields
        : undefined;
      return {
        collectionId: collection.queryName,
        ...(add && {
          updateSchema: (defs: NewFieldDefinition[]) => add(defs.map(toField)),
        }),
      };
    },
    mergeFields: (f: Schema['fields']) => fieldsSource?.mergeFields?.(f),
    ...(backup && { backup }),
    ...(idFieldKey && { idFieldKey }),
    ...(jobApplicationErrorMap && { jobApplicationErrorMap }),
  };
};

export interface ImportStateProps {
  /**
   * A pre-built import config. Usually omitted — pass `fieldsSource` + `importInputs`
   * instead and `ImportState` builds it. Kept for a fully hand-built config.
   */
  config?: ResolvedImportConfig;
  /** The table's live fields source; the built config reads fields/create/merge
   *  from it (unless a pre-built `config` is passed). */
  fieldsSource?: FieldsSource;
  /** The consumer inputs that aren't derived from the source. */
  importInputs?: ImportConfig;
  /** Wix httpClient used to drive the Hub Job (data-movement) API. */
  httpClient: HttpClient;
  /**
   * Infers a new field's type from its column data. Defaults to the primitive
   * detector (number / date / boolean / url / text). `@wix/patterns` injects a
   * media-aware detector from `@wix/patterns-fields` (image / video / document /
   * audio) — that detection needs the media URI parser, which core can't depend
   * on.
   */
  detectColumnType?: DetectColumnType;
  collection: ImportCollection;
  importModalState: ModalState;
  translate: Translate;
  errorHandler: PatternsErrorHandler;
  showToast?: ShowToast;
  downloadCsv?: DownloadCsvFn;
}

export type ImportStep = 'upload' | 'configuration' | 'progress' | 'summary';

// How long to hold the completed (100%) progress bar on screen before moving to
// the summary step (CAIRO-4476). The job reports counts only when it finishes,
// so the bar sits at 0 for the whole import and would otherwise flip straight to
// the summary without the user ever seeing it reach completion.
const COMPLETION_LINGER_MS = 600;

// The Wix Data collection's id field key. A CSV column mapped to this field lets
// the user choose how rows that collide on `_id` are written (writePolicy).
const ID_FIELD_KEY = '_id';

function normalize(value: string) {
  return value.trim().toLowerCase();
}

// camelCase key for a field created from a column header
// (e.g. "Product Name" -> "productName").
function toFieldKey(header: string): string {
  const words = header
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);
  if (words.length === 0) {
    return 'field';
  }
  return words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join('');
}

// A display name + key for a field created from a column header, unique against
// both `usedNames` (existing field display names + names already assigned to
// earlier new columns) and `usedKeys` (existing field ids + keys already
// assigned). Mirrors cm-import `generateFieldNameAndKey`: when the name or its
// derived key collides, suffix the *display name* as `"<header>-<n>"` and
// re-derive the key from it — so the created field's name and key are both
// unique (otherwise two columns with the same header would create fields showing
// the same name).
function uniqueFieldNameAndKey(
  header: string,
  usedNames: Set<string>,
  usedKeys: Set<string>,
): { displayName: string; key: string } {
  let displayName = header;
  for (let suffix = 1; ; suffix++) {
    const key = toFieldKey(displayName);
    if (!usedNames.has(displayName) && !usedKeys.has(key)) {
      return { displayName, key };
    }
    displayName = `${header}-${suffix}`;
  }
}

export class ImportState {
  // The import inputs, held by reference so a live schema-backed config's getters
  // stay live (never spread). The public getters below read from it on demand.
  private readonly config: ResolvedImportConfig;
  readonly collection: ImportCollection;
  readonly importModalState: ModalState;
  readonly translate: Translate;
  readonly errorHandler: PatternsErrorHandler;
  readonly showToast?: ShowToast;
  readonly downloadCsv?: DownloadCsvFn;
  private readonly hubJobClient: HubJobClient;
  private readonly _detectColumnType: DetectColumnType;
  readonly discardChangesModal = new ConditionalModalState<
    'goBack' | 'close'
  >();

  currentStep: ImportStep = 'upload';
  importStatus: TaskStatus<ImportResult, unknown> = {
    ...queryStatus.idle,
  };
  fileName = '';
  csvHeaders: string[] = [];
  csvRows: string[][] = [];
  // Header display order for the configuration step, snapshotted when the file
  // is parsed. The column auto-mapped to `_id` floats to the top (CAIRO-4418);
  // the order then stays fixed while the user edits mappings, so clearing or
  // remapping the `_id` column can't reorder the row under the user's cursor.
  // Recomputed only on a new file (`applyParsedCsv`) or a full `reset()`.
  orderedHeaders: string[] = [];
  mappingByHeader: Record<string, ColumnMapping> = {};
  // How rows that collide on `_id` are written. Only meaningful (and only sent
  // to the server) when a CSV column is mapped to the `_id` field — see
  // `idMappedHeader` and `_destination`. Defaults to the server default
  // (`OVERWRITE` = upsert by `_id`).
  //
  // Retention: the choice is deliberately kept while editing mappings within a
  // file — remapping `_id` away and back keeps the user's selection rather than
  // silently reverting it. It resets to the default only on a *new file* upload
  // (`applyParsedCsv`) or a full `reset()`.
  writePolicy: WritePolicy = WritePolicy.OVERWRITE;
  processedCount = 0;
  totalCount = 0;
  result: ImportResult = {
    succeeded: 0,
    failed: 0,
    created: 0,
    updated: 0,
  };
  isBackingUp = false;
  isRestoring = false;
  private restoreFn: ((signal: AbortSignal) => Promise<void>) | null = null;
  private _cancelled = false;
  private _abortController: AbortController | null = null;
  // Id of the running data-movement job, set once submitted — used to cancel it
  // (`terminateJob`) if the user closes mid-import.
  private _jobId: string | null = null;
  // The column whose pending new field the field editor is currently editing, or
  // `null` when the editor is closed. Observable so the source's field-editor
  // component (rendered via `renderFieldEditor`) opens/prefills itself when this
  // is set and closes when it clears — the editor is decoupled from the layout.
  editingHeader: string | null = null;
  // Holds `updateSchema`'s returned fields map between field creation (pre-upload)
  // and success. Kept private/non-observable so it can't update columns mid-import;
  // it's promoted to the observable `createdFieldsMap` only on success.
  private _pendingFieldsMap?: Schema['fields'];

  constructor(props: ImportStateProps) {
    this.collection = props.collection;
    // A pre-built config wins (hand-built / tests); otherwise assemble it from
    // the live source + inputs.
    this.config =
      props.config ??
      buildImportConfig(
        props.fieldsSource,
        props.collection,
        props.importInputs,
      );
    this.importModalState = props.importModalState;
    this.translate = props.translate;
    this.errorHandler = props.errorHandler;
    this.showToast = props.showToast;
    this.downloadCsv = props.downloadCsv;
    this._detectColumnType = props.detectColumnType ?? detectColumnType;
    this.hubJobClient = createHubJobClient({
      httpClient: props.httpClient,
      errorHandler: props.errorHandler,
    });

    makeObservable(this, {
      currentStep: observable.ref,
      importStatus: observable.ref,
      fileName: observable.ref,
      csvHeaders: observable.ref,
      csvRows: observable.ref,
      orderedHeaders: observable.ref,
      mappingByHeader: observable.ref,
      writePolicy: observable.ref,
      processedCount: observable.ref,
      totalCount: observable.ref,
      result: observable.ref,
      isBackingUp: observable.ref,
      isRestoring: observable.ref,
      hasParsedRows: computed,
      hasMappings: computed,
      hasNoMappedData: computed,
      selectedFieldIds: computed,
      missingRequiredFields: computed,
      hasMissingRequiredFields: computed,
      idMappedHeader: computed,
      editingHeader: observable.ref,
      editingMapping: computed,
      endFieldEdit: action.bound,
      setWritePolicy: action.bound,
      onUploadContinue: action.bound,
      goToUpload: action.bound,
      setMapping: action.bound,
      setNewFieldMapping: action.bound,
      setNewFieldConfig: action.bound,
      beginFieldEdit: action.bound,
      applyFieldEdit: action.bound,
      applyDataExtensionFieldEdit: action.bound,
      close: action.bound,
      done: action.bound,
      downloadFailedRows: action.bound,
      onFileSelected: action.bound,
      startImport: action.bound,
      reset: action.bound,
    });
  }

  // Import inputs read live from `config` — a live schema-backed config exposes
  // these as getters, a manual config as plain values; both read the same way.

  /** Existing collection fields, for column auto-mapping. */
  get fields(): ImportField[] {
    return this.config.fields;
  }

  /** Wix Data collection the import writes into (unset for platformized). */
  get collectionId(): string | undefined {
    const ds = this.config.dataSource;
    return 'collectionId' in ds ? ds.collectionId : undefined;
  }

  get destination(): Destination | undefined {
    const ds = this.config.dataSource;
    return 'destination' in ds ? ds.destination : undefined;
  }

  get backup(): BackupFn | undefined {
    return this.config.backup;
  }

  /** CMS create-fields callback, present only on the `collectionId` branch. */
  get updateSchema(): UpdateSchemaFn | undefined {
    const ds = this.config.dataSource;
    return 'collectionId' in ds ? ds.updateSchema : undefined;
  }

  /** DataExtension create-fields callback, present only on the `destination` branch. */
  get createDataExtensionFields(): CreateDataExtensionFieldsFn | undefined {
    const ds = this.config.dataSource;
    return 'destination' in ds ? ds.createFields : undefined;
  }

  /** DataExtension field-management for the editor, on the `destination` branch. */
  get dataExtensionFieldManagement(): FieldManagement | undefined {
    const ds = this.config.dataSource;
    return 'destination' in ds ? ds.fieldManagement : undefined;
  }

  get jobApplicationErrorMap(): JobApplicationErrorMap | undefined {
    return this.config.jobApplicationErrorMap;
  }

  // The collection's id-field key; a schema-backed config may report a non-`_id`
  // identifier. Defaults to the Wix Data `_id`.
  private get idFieldKey(): string {
    return this.config.idFieldKey ?? ID_FIELD_KEY;
  }

  init() {
    return () => {
      // Unmounting mid-import stops the poll loop (it checks `_cancelled`) so it
      // doesn't run to terminal holding references. We deliberately do NOT abort
      // the AbortController: during a cancel-triggered restore it holds the
      // restore controller, and aborting it would cancel an in-progress restore.
      // The server-side job is also left running — an unmount isn't an explicit
      // user cancel.
      this._cancelled = true;
    };
  }

  get hasParsedRows() {
    return this.csvHeaders.length > 0 && this.csvRows.length > 0;
  }

  get hasMappings() {
    return Object.values(this.mappingByHeader).some((value) => value != null);
  }

  /**
   * True when there are mappings but none of the mapped columns contain any
   * data in any row (including the case of zero data rows). Drives the
   * non-blocking "no rows found" warning in the configuration step. Returns
   * false when nothing is mapped yet — that case is handled by the disabled
   * import button instead.
   */
  get hasNoMappedData(): boolean {
    if (!this.hasMappings) {
      return false;
    }

    const mappedIndices = this.csvHeaders.reduce<number[]>(
      (indices, header, index) => {
        if (this.mappingByHeader[header] != null) {
          indices.push(index);
        }
        return indices;
      },
      [],
    );

    return !this.csvRows.some((row) =>
      mappedIndices.some((index) => (row[index] ?? '').trim() !== ''),
    );
  }

  get selectedFieldIds(): Set<string> {
    return new Set(
      Object.values(this.mappingByHeader)
        .filter(
          (m): m is { kind: 'existing'; fieldId: string } =>
            m?.kind === 'existing',
        )
        .map((m) => m.fieldId),
    );
  }

  /**
   * Required destination fields that no CSV column is mapped to (CAIRO-4452).
   * A required field is an existing collection field, so only an `existing`
   * mapping can satisfy it — a "create new field" column can't. Covers both
   * ways a required field ends up unmapped: the CSV has no matching column at
   * all, or the user set its column to "don't import". Drives the footer
   * indicator and blocks `startImport`.
   */
  get missingRequiredFields(): ImportField[] {
    return this.fields.filter(
      (field) =>
        field.required &&
        field.id != null &&
        !this.selectedFieldIds.has(field.id),
    );
  }

  get hasMissingRequiredFields(): boolean {
    return this.missingRequiredFields.length > 0;
  }

  // Comma-separated display names of the missing required fields, for the block
  // toast. Falls back to the field id when a field carries no header.
  private _missingRequiredFieldNames(): string {
    return this.missingRequiredFields
      .map((field) => field.header || field.id || '')
      .filter(Boolean)
      .join(', ');
  }

  /**
   * The CSV header currently mapped to the collection's `_id` field, or `null`
   * when no column targets it. Drives both the "When items share the same ID"
   * control (only shown on this row) and whether `writePolicy` is sent at all —
   * with no `_id` mapped, every row is a fresh insert and collisions can't occur.
   */
  get idMappedHeader(): string | null {
    const idFieldKey = this.idFieldKey;
    const entry = Object.entries(this.mappingByHeader).find(
      ([, mapping]) =>
        mapping?.kind === 'existing' && mapping.fieldId === idFieldKey,
    );
    return entry ? entry[0] : null;
  }

  setWritePolicy(policy: WritePolicy) {
    this.writePolicy = policy;
  }

  /**
   * Whether columns may be mapped to a brand-new field created during import.
   * The opt-in is the presence of the active `dataSource` branch's create
   * callback — `updateSchema` (CMS) or `createFields` (DataExtension). A source
   * that can't create fields (e.g. no create permission) supplies neither, so
   * columns fall back to "don't import" (see `getDefaultMapping`).
   */
  get canCreateNewField(): boolean {
    return this.updateSchema != null || this.createDataExtensionFields != null;
  }

  /**
   * Whether the write-policy (id-collision) choice is meaningful for this import.
   * `writePolicy` is a `wixDataCollection`-only concept, and `_destination` only
   * attaches it when Cairo builds that destination itself. A consumer-supplied
   * (platformized) `destination` is sent verbatim with no place to carry it, so
   * the choice would be a no-op — the UI hides the control in that case. Fixed at
   * construction (depends only on the `destination` prop), so it isn't an
   * observable.
   */
  get canConfigureWritePolicy(): boolean {
    return this.destination == null;
  }

  reset() {
    this._abortController?.abort();
    this._abortController = null;
    this.currentStep = 'upload';
    this.importStatus = {
      ...queryStatus.idle,
    };
    this.fileName = '';
    this.csvHeaders = [];
    this.csvRows = [];
    this.orderedHeaders = [];
    this.mappingByHeader = {};
    this.writePolicy = WritePolicy.OVERWRITE;
    this.processedCount = 0;
    this.totalCount = 0;
    this.result = {
      succeeded: 0,
      failed: 0,
      created: 0,
      updated: 0,
    };
    this._pendingFieldsMap = undefined;
    this.isBackingUp = false;
    this.isRestoring = false;
    this.restoreFn = null;
    this._cancelled = false;
    this._jobId = null;
  }

  async close() {
    // While on the progress step we treat close as a cancel — terminate the job
    // and restore the backup — UNLESS the import already succeeded. On success
    // `startImport` moves straight to the 'summary' step, so a close from there
    // just closes the modal (the summary's own `done` is the normal exit); this
    // guard covers only closing mid-import.
    if (this.currentStep === 'progress' && !this.importStatus.isSuccess) {
      this._cancelled = true;
      this._abortController?.abort();

      // Stop the server-side job. It does not roll back rows already written —
      // that's what the backup restore below is for.
      if (this._jobId) {
        try {
          await this.hubJobClient.terminateJob(this._jobId);
        } catch (error) {
          console.error('Terminate import job failed', error);
        }
      }

      if (!this.isBackingUp) {
        await this._restoreBackup();
      }

      this.collection.clearResultAndMoveToStart({
        force: true,
      });
    }

    this.importModalState.close();
  }

  /**
   * Closes the summary step at the end of a completed import. The collection was
   * already refreshed when the summary opened, so this only dismisses the modal.
   */
  done() {
    this.importModalState.close();
  }

  private async _restoreBackup() {
    const restoreFn = this.restoreFn;
    if (!restoreFn) {
      return;
    }

    const restoreController = new AbortController();
    this._abortController = restoreController;

    this.isRestoring = true;
    try {
      await restoreFn(restoreController.signal);
    } catch (error) {
      console.error('Restore failed', error);
    }
    runInAction(() => {
      this.isRestoring = false;
      this.restoreFn = null;
    });
  }

  goToUpload() {
    this.currentStep = 'upload';
  }

  async discardAndGoBack() {
    this.discardChangesModal.open('goBack');
    const { result } = await this.discardChangesModal.modal.promise();
    if (result === 'confirmed') {
      this.goToUpload();
    }
  }

  async discardAndClose() {
    this.discardChangesModal.open('close');
    const { result } = await this.discardChangesModal.modal.promise();
    if (result === 'confirmed') {
      this.close();
    }
  }

  onUploadContinue() {
    if (this.hasParsedRows) {
      this.currentStep = 'configuration';
    }
  }

  private getDefaultMapping(headers: string[]) {
    const mapping: Record<string, ColumnMapping> = {};
    const fields = this.fields.map((field) => ({
      id: field.id ?? '',
      header: field.header ?? '',
    }));

    headers.forEach((header, index) => {
      const normalizedHeader = normalize(header);
      const matched = fields.find(
        (field) =>
          normalize(field.id) === normalizedHeader ||
          normalize(field.header) === normalizedHeader,
      );

      if (matched?.id) {
        mapping[header] = { kind: 'existing', fieldId: matched.id };
      } else {
        // Unmatched columns default to creating a new field (type inferred from
        // the column's data) when the consumer supplied `updateSchema`;
        // otherwise they default to skip.
        mapping[header] = this.canCreateNewField
          ? {
              kind: 'new',
              fieldType: this._detectColumnType(this._columnValues(index)),
            }
          : null;
      }
    });

    return mapping;
  }

  // The column's values, capped at the detection sample size — detection only
  // looks at the first `TYPE_SAMPLE_SIZE` values, so avoid copying the whole
  // column (a wide CSV would otherwise allocate rows × columns strings).
  private _columnValues(columnIndex: number): string[] {
    return this.csvRows
      .slice(0, TYPE_SAMPLE_SIZE)
      .map((row) => row[columnIndex] ?? '');
  }

  // Puts the `_id`-mapped column first (CAIRO-4418), preserving CSV order for the
  // rest. Returns a fresh array so the snapshot doesn't alias `csvHeaders`.
  private _orderHeadersWithIdFirst(headers: string[]): string[] {
    const idHeader = this.idMappedHeader;
    return idHeader != null
      ? [idHeader, ...headers.filter((header) => header !== idHeader)]
      : [...headers];
  }

  private applyParsedCsv(fileName: string, parsedCsv: ParseCsvResult) {
    this.fileName = fileName;
    this.csvHeaders = parsedCsv.headers;
    this.csvRows = parsedCsv.rows;
    this.mappingByHeader = this.getDefaultMapping(parsedCsv.headers);
    // Snapshot the row order for this file with the auto-mapped `_id` column
    // first. Reads `idMappedHeader`, so it must run after `mappingByHeader` is
    // set. Kept stable until the next file so mapping edits don't reorder rows.
    this.orderedHeaders = this._orderHeadersWithIdFirst(parsedCsv.headers);
    // A new file is a fresh import: start from the default policy so a prior
    // file's Skip choice doesn't silently carry over (only the new-file path
    // resets — remapping `_id` within the same file keeps the user's choice).
    this.writePolicy = WritePolicy.OVERWRITE;
    this.currentStep = 'configuration';
  }

  async onFileSelected(file: File | null) {
    if (file == null) {
      return;
    }

    const bytes = await this._readFileBytes(file);
    if (bytes == null) {
      this._showFileReadErrorToast(file);
      return;
    }

    const content = this._decodeUtf8(bytes);
    if (content == null) {
      this._rejectFile(file, 'cairo.import.encodingFailed');
      return;
    }

    const parsedCsv = parseCsv(content);
    if ('code' in parsedCsv) {
      this._rejectFile(file, 'cairo.import.processFailed');
      return;
    }

    runInAction(() => {
      this.applyParsedCsv(file.name, parsedCsv);
    });
  }

  private async _readFileBytes(file: File): Promise<ArrayBuffer | null> {
    try {
      return await file.arrayBuffer();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private _decodeUtf8(bytes: ArrayBuffer): string | null {
    try {
      // `fatal: true` throws on any byte sequence that isn't valid UTF-8,
      // so encoding is validated against the raw bytes before decoding —
      // unlike `File.text()`, which silently substitutes U+FFFD.
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private _rejectFile(file: File, messageKey: TranslationsKeys) {
    runInAction(() => {
      this.fileName = file.name;
      this.csvHeaders = [];
      this.csvRows = [];
      this.orderedHeaders = [];
      this.mappingByHeader = {};
      this.currentStep = 'upload';
    });
    this.showToast?.({
      type: 'ERROR',
      message: this.translate(messageKey),
    });
  }

  private _showFileReadErrorToast(file: File) {
    this.showToast?.({
      type: 'ERROR',
      message: this.translate('cairo.import.fileReadFailed'),
      action: {
        text: this.translate('cairo.import.tryAgain'),
        onClick: () => this.onFileSelected(file),
        removeToastOnClick: true,
      },
    });
  }

  /**
   * Builds a re-importable CSV of the rows the server rejected and hands it to
   * `downloadCsv`. Each failed row is prefixed with a leading "why it failed"
   * column holding the server's error message, so the user can see and fix each
   * reason. The failed rows (and their reasons) come from the job's movement
   * logs, mapped back to the parsed `csvRows` we still hold in memory.
   */
  async downloadFailedRows() {
    const jobId = this._jobId;
    if (!jobId) {
      return;
    }

    let failures: FailedRow[];
    try {
      failures = await this._fetchFailedRows(jobId);
    } catch (error) {
      console.error('Fetching failed rows report failed', error);
      return;
    }

    const rows = failures
      .map(({ index, reason }) => {
        const row = this.csvRows[index];
        return row != null ? [reason, ...row] : null;
      })
      .filter((row): row is string[] => row != null);

    if (rows.length === 0) {
      return;
    }

    const headers = [
      this.translate('cairo.import.failureReasonColumn'),
      ...this.csvHeaders,
    ];
    const content = unparseCsv(headers, rows);
    this.downloadCsv?.(content, `failed-${this.fileName || 'import.csv'}`);
  }

  // Pages through the job's movement logs and returns, for each row that failed,
  // its `csvRows` index and the server's failure reason. data-hub's CSV decoder
  // (wix-data-hub: packages/wix-data-hub-executor-c/src/plugins/formats/csv/
  // csv-binary-decoder.ts) parses with the `csv-parse` library (`columns: true`)
  // and sets `sourceItemId = "row ${++index + 1}"`, incrementing once **per
  // record** — not per physical line. So N is the 1-based record number with
  // the header consumed as columns: the first data record is `"row 2"` →
  // `csvRows[0]`, i.e. `csvRows[N - 2]`. Because `csv-parse` is RFC-4180,
  // quoted cells containing embedded newlines are a single record, so they do
  // NOT shift the numbering. Out-of-range indices are guarded below.
  private async _fetchFailedRows(jobId: string): Promise<FailedRow[]> {
    const failures: FailedRow[] = [];
    let cursor: string | undefined;

    do {
      const { logs, pagingMetadata } = await this.hubJobClient.listMovementLogs(
        jobId,
        cursor ? { cursor } : undefined,
      );

      for (const log of logs ?? []) {
        if (!log.failure || log.sourceItemId == null) {
          continue;
        }
        const fileLine = Number(/\d+/.exec(log.sourceItemId)?.[0]);
        const index = fileLine - 2;
        if (
          Number.isInteger(index) &&
          index >= 0 &&
          index < this.csvRows.length
        ) {
          failures.push({ index, reason: log.failure.description ?? '' });
        }
      }

      cursor =
        (pagingMetadata?.hasNext && pagingMetadata.cursors?.next) || undefined;
    } while (cursor);

    return failures;
  }

  setMapping(header: string, fieldId: string | null) {
    this.mappingByHeader = {
      ...this.mappingByHeader,
      [header]: fieldId == null ? null : { kind: 'existing', fieldId },
    };
  }

  setNewFieldMapping(header: string) {
    const index = this.csvHeaders.indexOf(header);
    const fieldType =
      index >= 0 ? this._detectColumnType(this._columnValues(index)) : 'text';
    this.mappingByHeader = {
      ...this.mappingByHeader,
      [header]: { kind: 'new', fieldType },
    };
  }

  /**
   * Persists the result of the "Edit field" editor for a column's pending new
   * field: the (possibly overridden) type, an optional display-name override,
   * and the per-type configuration. No-op if the column isn't currently mapped
   * to a new field.
   */
  setNewFieldConfig(
    header: string,
    edit: {
      fieldType: NewFieldType;
      displayName?: string;
      config?: NewFieldConfig;
    },
  ) {
    const mapping = this.mappingByHeader[header];
    if (mapping?.kind !== 'new') {
      return;
    }
    this.mappingByHeader = {
      ...this.mappingByHeader,
      [header]: {
        kind: 'new',
        fieldType: edit.fieldType,
        ...(edit.displayName != null && { displayName: edit.displayName }),
        ...(edit.config && { config: edit.config }),
      },
    };
  }

  /**
   * Opens the field editor for a column's pending new field: sets
   * `editingHeader`, which the source's editor component watches to open and
   * prefill itself. No-op if the column isn't mapped to a new field.
   */
  beginFieldEdit(header: string) {
    if (this.mappingByHeader[header]?.kind === 'new') {
      this.editingHeader = header;
    }
  }

  /** Closes the field editor (clears `editingHeader`). */
  endFieldEdit() {
    this.editingHeader = null;
  }

  /**
   * The `new` mapping currently being edited, or `null` when the editor is
   * closed. The editor component reads this to prefill its form.
   */
  get editingMapping(): Extract<ColumnMapping, { kind: 'new' }> | null {
    if (this.editingHeader == null) {
      return null;
    }
    const mapping = this.mappingByHeader[this.editingHeader];
    return mapping?.kind === 'new' ? mapping : null;
  }

  /**
   * `onSubmit` handler for the CMS field editor: maps the edited field spec into
   * the column being edited (set via `beginFieldEdit`). No-op if nothing is being
   * edited.
   */
  applyFieldEdit(field: FieldSpec) {
    if (this.editingHeader == null) {
      return;
    }
    this.setNewFieldConfig(this.editingHeader, toFieldEdit(field));
  }

  /**
   * `onSubmit` handler for the DataExtension field editor: maps the edited
   * DataExtension draft (which carries per-audience permissions) into the column
   * being edited. The `destination`-branch counterpart to `applyFieldEdit`.
   */
  applyDataExtensionFieldEdit(draft: FieldDraft) {
    if (this.editingHeader == null) {
      return;
    }
    this.setNewFieldConfig(this.editingHeader, toDataExtensionFieldEdit(draft));
  }

  /**
   * Runs the server-side import: align the CSV to the resolved field keys,
   * upload it, submit a data-movement job, then poll until terminal. Throws on
   * a failed job so `startImport` surfaces the error state; partial success is
   * reported via `result.failed` and the partial toast.
   */
  private async _runImportJob(
    resolvedMapping: Record<string, string | null>,
  ): Promise<ImportResult> {
    const csv = rewriteParsedCsvForImport(
      this.csvHeaders,
      this.csvRows,
      resolvedMapping,
    );
    const file = new File([csv], this.fileName || 'import.csv', {
      type: 'text/csv',
    });

    const fileId = await this.hubJobClient.uploadFile(file);
    if (this._cancelled) {
      return this.result;
    }

    const jobId = await this.hubJobClient.submitImportJob({
      fileId,
      name: this.fileName,
      destination: this._destination(),
    });
    runInAction(() => {
      this._jobId = jobId;
    });
    if (this._cancelled) {
      // The user cancelled while `submitImportJob` was in flight, so `close()`
      // saw no `_jobId` and couldn't terminate it. Terminate it here, otherwise
      // the job keeps importing server-side after cancel (and would overwrite a
      // restored backup).
      await this.hubJobClient.terminateJob(jobId).catch(() => undefined);
      return this.result;
    }

    const job = await pollImportJob(
      (id) => this.hubJobClient.getJob(id),
      jobId,
      {
        isCancelled: () => this._cancelled,
        onProgress: (polled) => this._applyJobProgress(polled),
      },
    );

    // `null` means cancelled mid-poll — leave whatever progress we have.
    if (!job) {
      return this.result;
    }

    if (job.status === JobStatus.FAILED) {
      // The job ran server-side, so a failure arrives on a 200 (the poll
      // succeeded; the job didn't) with no HTTP error to carry the application
      // error. Build a synthetic, HTTP-error-shaped error (the error handler
      // reads the code from `response.data.details.applicationError`), then
      // resolve it through the consumer's per-code map via `withErrorHandler` —
      // the same way as `fetchData` etc. `job.error` is the consumer's
      // application error (their import business logic), so only they can map
      // its codes; they supply just the code→message map and we do the
      // wrapping. `withErrorHandler` attaches the map to the error and
      // re-rejects it, so the progress step resolves a consumer-specific
      // message via `getResolvedError`. Without a map the same error still
      // throws, falling back to the generic message.
      const error = Object.assign(
        new Error(`ImportJob.${job.error?.code ?? 'failed'}`),
        { response: { data: { details: { applicationError: job.error } } } },
      );

      const jobApplicationErrorMap = this.jobApplicationErrorMap;
      if (jobApplicationErrorMap) {
        // Adapt the consumer's code→message map into the error handler's
        // `errorCodesMap`: each entry, when its code matches the job's
        // application error, resolves with that error. `withErrorHandler`
        // attaches the resolved message to `error` and re-rejects it, so the
        // progress step surfaces it via `getResolvedError` — the consumer only
        // supplies the map, not the wrapping.
        const appError = (job.error ?? {}) as ImportJobApplicationError;
        const errorCodesMap = Object.fromEntries(
          Object.entries(jobApplicationErrorMap).map(([code, resolve]) => [
            code,
            () => resolve(appError),
          ]),
        );
        await this.errorHandler.withErrorHandler(
          (): Promise<unknown> => Promise.reject(error),
          errorCodesMap,
        );
      }
      throw error;
    }

    this._applyJobProgress(job);
    return this.result;
  }

  // Destination the job writes into. A platformized consumer supplies its own
  // variant (e.g. `{ storesCatalogProducts: {} }`), which is sent verbatim.
  // Otherwise this is the consumer's Wix Data collection, built from
  // `collectionId`; `writePolicy` is sent only when a CSV column is mapped to
  // `_id` (the only case collisions can occur), otherwise it's omitted and the
  // server applies its default (upsert by `_id`).
  private _destination(): Destination {
    if (this.destination) {
      return this.destination;
    }
    return {
      wixDataCollection: {
        collectionId: this.collectionId,
        ...(this.idMappedHeader != null && { writePolicy: this.writePolicy }),
      },
    };
  }

  private _applyJobProgress(job: DataMovementJob) {
    const progress = job.progress;
    const succeeded = progress?.itemsSuccessful ?? 0;
    const failed = progress?.itemsRejected ?? 0;
    const current = progress?.current ?? succeeded + failed;
    // The server splits successful items into created (new) and updated
    // (existing) — the summary step reports each. Some jobs omit this split
    // entirely (`itemsSuccessfulInfo` absent); in that case treat every success
    // as created so the summary still shows a count instead of a blank step.
    const successInfo = progress?.itemsSuccessfulInfo;
    const created = successInfo ? successInfo.itemsCreated ?? 0 : succeeded;
    const updated = successInfo?.itemsUpdated ?? 0;

    // `totalCount` is fixed up front in `startImport` from the CSV row count
    // (one uploaded row per CSV row); polling only advances `processedCount`.
    runInAction(() => {
      this.processedCount = Math.min(current, this.totalCount);
      this.result = { succeeded, failed, created, updated };
    });
  }

  private _lingerOnCompletion(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, COMPLETION_LINGER_MS));
  }

  private async _runBackup(): Promise<boolean> {
    const backup = this.backup;
    if (!backup) {
      return false;
    }

    try {
      const result = await backup(this._abortController!.signal);
      if (this._cancelled) {
        return true;
      }
      if (result?.restore) {
        this.restoreFn = result.restore;
      }
    } catch (error) {
      if (this._cancelled) {
        return true;
      }
      runInAction(() => {
        this.isBackingUp = false;
        this.importStatus = {
          ...queryStatus.error,
          error,
        };
      });
      return true;
    }

    runInAction(() => {
      this.isBackingUp = false;
    });
    return false;
  }

  /**
   * Resolves every column to the final destination key used in the upload
   * payload: an existing field id, a new field's (Cairo-decided) key, or `null`
   * to skip. All new fields are added to the schema in a single `updateSchema`
   * call before the rows upload — mirroring the cm-import `beforeImport` flow.
   * `updateSchema` may throw — `startImport` surfaces that as the error state.
   */
  private async _resolveFieldMapping(): Promise<Record<string, string | null>> {
    const resolved: Record<string, string | null> = {};
    // Pending new columns with their deduped key + display name. Source-neutral;
    // the per-source field def is built from these in `_createPendingFields`.
    const pending: PendingNewField[] = [];
    // Seed with existing field ids/display names so derived keys and names never
    // collide with them or with each other.
    const usedKeys = new Set(
      this.fields.map((field) => field.id).filter((id): id is string => !!id),
    );
    const usedNames = new Set(
      this.fields
        .map((field) => field.header)
        .filter((header): header is string => !!header),
    );
    const canCreate = this.canCreateNewField;

    for (const header of this.csvHeaders) {
      const mapping = this.mappingByHeader[header];

      if (mapping == null) {
        resolved[header] = null;
      } else if (mapping.kind === 'existing') {
        resolved[header] = mapping.fieldId;
      } else if (canCreate) {
        // The user's name override (from the field editor) seeds the unique
        // name/key derivation; otherwise fall back to the column header.
        const baseName = mapping.displayName?.trim() || header;
        const { displayName, key } = uniqueFieldNameAndKey(
          baseName,
          usedNames,
          usedKeys,
        );
        usedKeys.add(key);
        usedNames.add(displayName);
        pending.push({ key, displayName, mapping });
        resolved[header] = key;
      } else {
        // "new" mapping with no create callback on this source — nothing to map into.
        resolved[header] = null;
      }
    }

    if (pending.length > 0) {
      // Stash the returned fields map to merge into the table on success; kept off
      // the observable until then so columns don't update mid-import.
      this._pendingFieldsMap = await this._createPendingFields(pending);
    }

    return resolved;
  }

  /**
   * Creates the pending new fields via the active `dataSource` branch's callback
   * — `updateSchema` (CMS) or `createFields` (DataExtension) — building the
   * source-specific field def from the shared, already-deduped pending columns.
   * The callback may throw; `startImport` surfaces that as the error state.
   */
  private async _createPendingFields(
    pending: PendingNewField[],
  ): Promise<Schema['fields'] | undefined> {
    const updateSchema = this.updateSchema;
    const createDataExtensionFields = this.createDataExtensionFields;

    let updated: Schema['fields'] | void;
    if (updateSchema) {
      updated = await updateSchema(
        pending.map(({ key, displayName, mapping }) => ({
          key,
          type: mapping.fieldType,
          displayName,
          ...(mapping.config && { config: mapping.config }),
        })),
      );
    } else if (createDataExtensionFields) {
      // The DataExtension source builds its own field shape from the neutral
      // pending columns — no DataExtension-specific def-building in core.
      updated = await createDataExtensionFields(pending);
    } else {
      return undefined;
    }

    // Guard the empty map: SchemaState.mergeFields is a full replace, so merging
    // `{}` would wipe every column — treat a missing/empty return as "nothing to
    // merge".
    return updated && Object.keys(updated).length > 0 ? updated : undefined;
  }

  async startImport() {
    if (!this.hasMappings) {
      return;
    }

    // Block import while any required destination field is unmapped and tell the
    // user which (CAIRO-4452). The footer already flags this; the Import button
    // stays enabled, so this is where the click is stopped.
    if (this.hasMissingRequiredFields) {
      this.showToast?.({
        type: 'ERROR',
        message: this.translate('cairo.import.missingRequiredMessage', {
          fields: this._missingRequiredFieldNames(),
        }),
      });
      return;
    }

    this._cancelled = false;
    this._abortController = new AbortController();

    // One uploaded row per CSV row (the rewrite drops columns, not rows), so the
    // total is known up front from the parsed CSV and stays fixed through polling.
    const totalCount = this.csvRows.length;

    runInAction(() => {
      this.currentStep = 'progress';
      this.totalCount = totalCount;
      this.processedCount = 0;
      this.isBackingUp = !!this.backup;
      // Clear any error/result from a previous attempt so "Try again" starts
      // from a clean slate (e.g. after a backup failure).
      this.importStatus = { ...queryStatus.idle };
      this.result = {
        succeeded: 0,
        failed: 0,
        created: 0,
        updated: 0,
      };
      this._jobId = null;
      // Drop any fields map from a prior attempt so a no-new-fields "Try again"
      // can't merge stale fields.
      this._pendingFieldsMap = undefined;
    });

    const backupFailed = await this._runBackup();
    if (backupFailed) {
      return;
    }
    if (this._cancelled) {
      return;
    }

    // Create any new fields and resolve every column to its destination key
    // before uploading. A creation failure surfaces as the error state with a
    // "Try again" CTA, like an upload/job failure.
    let resolvedMapping: Record<string, string | null>;
    try {
      resolvedMapping = await this._resolveFieldMapping();
    } catch (error) {
      runInAction(() => {
        this.importStatus = {
          ...queryStatus.error,
          error,
        };
      });
      return;
    }
    if (this._cancelled) {
      return;
    }

    const promise = this._runImportJob(resolvedMapping);

    this.importStatus = {
      ...queryStatus.loading,
      promise,
    };

    // Assigned only on the success path below, so the post-import merge (after the
    // try/catch) fires only for a completed import — without re-checking status
    // there. Reading `_pendingFieldsMap` directly wouldn't do: it's set pre-upload,
    // so it stays truthy even when the job later fails.
    let createdFields: Schema['fields'] | undefined;
    try {
      await promise;
      if (this._cancelled) {
        return;
      }

      runInAction(() => {
        this.importStatus = {
          ...queryStatus.success,
          data: this.result,
        };
        // The job reports counts only when it finishes, so the bar sat at 0 for
        // the whole import (CAIRO-4476). Snap it to 100% and hold it (below)
        // before the summary step replaces the progress step, so the user sees
        // the import reach completion.
        this.processedCount = this.totalCount;
      });

      // Keep the completed bar on screen briefly before moving on. `importStatus`
      // is already success, so a close during this window just dismisses the
      // modal (the `close()` guard doesn't treat it as a cancel).
      await this._lingerOnCompletion();
      if (this._cancelled) {
        return;
      }

      // Success path only. `_pendingFieldsMap` holds this run's returned map — it's
      // cleared at the start of `startImport` (the stale-guard) and set by
      // `_resolveFieldMapping`; snapshotting here keeps the merge success-gated.
      createdFields = this._pendingFieldsMap;

      runInAction(() => {
        // Import finished: show the summary step (created / updated / failed
        // counts, plus a "Download CSV" of the failed rows when any failed).
        // The modal stays open; the user dismisses it from the summary via
        // `done`. Refresh the collection now so the table behind the modal is
        // up to date by the time it closes.
        this.currentStep = 'summary';
        this.collection.clearResultAndMoveToStart({ force: true });
      });
    } catch (error) {
      runInAction(() => {
        this.importStatus = {
          ...queryStatus.error,
          error,
        };
      });
    }

    // Merge the created fields into the table so the new columns appear with no
    // manual reload (CAIRO-4400). Called imperatively here, outside the try/catch
    // above, so a merge failure can't flip a completed import into the error state
    // (whose "Try again" re-imports every row). `createdFields` is set only on the
    // success path and only when fields were created.
    if (createdFields) {
      try {
        this.config.mergeFields?.(createdFields);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ImportState config.mergeFields failed', error);
      }
    }
  }
}
